import { useEffect, useRef } from "react";
import type { GeolocationPosition, Position, TenantApp } from "@inloco/shared";
import { UPDATE_FREQUENCIES } from "@inloco/shared";
import type { RoomSession } from "../../types/room-session";
import { useAuth } from "../context";

interface UsePositionAutoUpdateProps {
    app: TenantApp | null;
    position: GeolocationPosition | null;
    roomSession: RoomSession | null;
    updatePosition: (app: TenantApp, position: Position, userId: string) => Promise<void>;
}

export const usePositionAutoUpdate = (props: UsePositionAutoUpdateProps): void => {
    const { app, position, roomSession, updatePosition } = props;

    const userSessionRef = useRef(roomSession);
    const { userFirebase } = useAuth();
    const lastPositionUpdateRef = useRef<number>(0);

    // Update ref when roomSession changes
    useEffect(() => {
        userSessionRef.current = roomSession;
    }, [roomSession]);

    useEffect(() => {
        const session = userSessionRef.current;
        if (!position || !userFirebase || !app || !session) return;

        const now = Date.now();
        const timeSinceLastUpdate = now - lastPositionUpdateRef.current;
        const frequencyUpdate = UPDATE_FREQUENCIES[app.subscription.updateFrequency];
        // Update only if at least x seconds have passed since last update
        if (timeSinceLastUpdate >= frequencyUpdate) {
            const { uid } = userFirebase;
            lastPositionUpdateRef.current = now;

            // Fire-and-forget: error is already handled internally by useRoom
            updatePosition(app, position, uid).catch(() => {
                // Error already handled by useRoom hook
            });
        }
    }, [position, app, userFirebase, updatePosition]);
};
